import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Music, Sparkles, Crown, Loader2, AlertCircle, ListMusic, ExternalLink, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PlaylistLiveCounter from '../components/PlaylistLiveCounter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlaylistPromoPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const tx = (es, en, pt) => isEs ? es : (isPt ? (pt || en) : en);
  const [plans, setPlans] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Submission form
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [trackTitle, setTrackTitle] = useState('');
  const [artistName, setArtistName] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/playlist-promo/plans`);
        setPlans(r.data.plans || []);
        setPlaylists(r.data.playlists || []);
      } catch {
        setError(tx('No se pudieron cargar los planes', 'Could not load plans', 'Não foi possível carregar os planos'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlePickPlan = (planId) => {
    setSelectedPlan(planId);
    setError('');
    setTimeout(() => {
      const el = document.getElementById('promo-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent('/playlist-promotion')}`);
      return;
    }
    if (!selectedPlan) {
      setError(tx('Elige un plan primero', 'Pick a plan first', 'Escolha um plano primeiro'));
      return;
    }
    if (!spotifyUrl.trim()) {
      setError(tx('Pega tu URL de Spotify', 'Paste your Spotify URL', 'Cole sua URL do Spotify'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/playlist-promo/submit`, {
        plan_id: selectedPlan,
        spotify_url: spotifyUrl.trim(),
        track_title: trackTitle.trim(),
        artist_name: artistName.trim(),
        notes: notes.trim(),
      }, { withCredentials: true });
      navigate(`/checkout?type=playlist_promo&id=${res.data.promo_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || tx('Error al crear la promoción', 'Error creating the promotion', 'Erro ao criar a promoção'));
      setSubmitting(false);
    }
  };

  const planIcon = (id) => id === 'starter' ? Music : id === 'pro' ? Sparkles : Crown;

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="playlist-promo-page">
      <div className="container-custom">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E8720C] mb-4">OG Playlist Promotion</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            {tx('Lleva tu música a', 'Bring your music to', 'Leve sua música para')} <span className="gold-text">{tx('nuestras playlists curadas', 'our curated playlists', 'nossas playlists curadas')}</span>
          </h1>
          <p className="text-lg text-white/65 leading-relaxed mb-6">
            {tx(
              '¿Tu canción ya está en Spotify? La incluimos en nuestra red de ',
              'Your song already on Spotify? We add it to our network of ',
              'Sua música já está no Spotify? Nós a incluímos em nossa rede de '
            )}<strong className="text-white">{tx('+100 playlists curadas a nivel mundial', '+100 curated playlists worldwide', '+100 playlists curadas mundialmente')}</strong>{tx(
              ' para que más oyentes la descubran. Solo pega tu URL de Spotify, elige plan y nosotros nos encargamos del resto.',
              ' so more listeners can discover it. Just paste your Spotify URL, pick a plan and we handle the rest.',
              ' para que mais ouvintes a descubram. Cole sua URL do Spotify, escolha o plano e nós cuidamos do resto.'
            )}
          </p>

          {/* Live counter — real Spotify data */}
          <div className="my-8">
            <PlaylistLiveCounter />
          </div>

          <div className="inline-flex flex-wrap items-center justify-center gap-2 px-4 py-2 rounded-full bg-green-500/10 ring-1 ring-green-500/30 text-green-200 text-xs font-semibold">
            <CheckCircle className="w-4 h-4" />
            <span>{tx('+100 playlists curadas activas en Spotify a nivel mundial', '+100 active curated playlists on Spotify worldwide', '+100 playlists curadas ativas no Spotify mundialmente')}</span>
          </div>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E8720C]" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {plans.map((p) => {
              const Icon = planIcon(p.id);
              const isPopular = p.popular;
              const isSelected = selectedPlan === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => handlePickPlan(p.id)}
                  className={`relative text-left glass-card p-7 hover:-translate-y-1 transition-all ${
                    isPopular ? 'border-[#E8720C]/40 ring-1 ring-[#E8720C]/20' : 'border-white/10'
                  } ${isSelected ? 'ring-2 ring-[#E8720C] border-[#E8720C]/60' : ''}`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {isPopular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-white rounded-full">
                      Más popular
                    </span>
                  )}
                  {isSelected && (
                    <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-green-500 text-white rounded-full">
                      <CheckCircle className="w-3 h-3" /> Seleccionado
                    </span>
                  )}

                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isPopular ? 'bg-[#E8720C]/15 ring-1 ring-[#E8720C]/30' : 'bg-white/5'}`}>
                      <Icon className={`w-5 h-5 ${isPopular ? 'text-[#E8720C]' : 'text-white/70'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{p.name}</h3>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{p.tagline_es}</p>
                    </div>
                  </div>

                  <div className="mb-5">
                    {p.promo_active && p.promo_discount_pct > 0 ? (
                      <>
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-4xl font-bold gold-text">${(p.effective_price ?? p.price).toFixed(2)}</span>
                          <span className="text-base text-white/40 line-through">${p.price.toFixed(2)}</span>
                          <span className="text-white/40 text-sm ml-1">/ track</span>
                        </div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[10px] font-bold uppercase tracking-wider ring-1 ring-red-500/40">
                          {p.promo_label_es || `-${p.promo_discount_pct}%`}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold gold-text">${p.price.toFixed(2)}</span>
                        <span className="text-white/40 text-sm ml-1">/ track</span>
                      </>
                    )}
                  </div>

                  <ul className="space-y-2.5 mb-5">
                    {(p.features_es || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPopular ? 'text-[#E8720C]' : 'text-green-400/80'}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className={`w-full py-2.5 text-center rounded-lg text-sm font-bold transition-colors ${
                    isSelected
                      ? 'bg-green-500/15 text-green-300 ring-1 ring-green-500/30'
                      : isPopular
                        ? 'bg-[#E8720C] hover:bg-[#C55A00] text-white'
                        : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                  }`}>
                    {isSelected
                      ? tx('✓ Plan elegido — completa abajo', '✓ Plan selected — fill in below', '✓ Plano escolhido — preencha abaixo')
                      : tx('Elegir este plan', 'Pick this plan', 'Escolher este plano')}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Submission Form */}
        <div id="promo-form" className="glass-card p-7 md:p-10 max-w-3xl mx-auto mb-16" data-testid="promo-form">
          <h2 className="text-2xl font-bold mb-2">{tx('Envíanos tu track', 'Send us your track', 'Envie seu track')}</h2>
          <p className="text-sm text-white/60 mb-6">
            {tx(
              'Pega la URL pública de tu canción en Spotify. Nuestro equipo curador la revisará y la incluirá en las playlists relevantes según el plan.',
              'Paste the public URL of your song on Spotify. Our curator team will review it and add it to the relevant playlists per your plan.',
              'Cole a URL pública da sua música no Spotify. Nossa equipe curadora vai revisá-la e incluí-la nas playlists relevantes conforme seu plano.'
            )}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{tx('Plan elegido', 'Selected plan', 'Plano escolhido')}</label>
              <div className={`px-4 py-3 rounded-xl ${selectedPlan ? 'bg-[#E8720C]/10 ring-1 ring-[#E8720C]/30 text-[#E8720C]' : 'bg-white/5 text-white/40'}`} data-testid="selected-plan-display">
                {selectedPlan
                  ? `${(plans.find(p => p.id === selectedPlan) || {}).name} · $${((plans.find(p => p.id === selectedPlan) || {}).effective_price ?? (plans.find(p => p.id === selectedPlan) || {}).price)?.toFixed(2)}`
                  : tx('— Elige un plan arriba —', '— Pick a plan above —', '— Escolha um plano acima —')}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{tx('URL de Spotify *', 'Spotify URL *', 'URL do Spotify *')}</label>
              <input
                type="url"
                value={spotifyUrl}
                onChange={(e) => setSpotifyUrl(e.target.value)}
                placeholder="https://open.spotify.com/track/..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#E8720C]/50 text-sm"
                required
                data-testid="spotify-url-input"
              />
              <p className="text-[10px] text-white/40 mt-1.5">{tx('Ejemplo:', 'Example:', 'Exemplo:')} https://open.spotify.com/track/4cOdK2wGLETKBW3PvgPWqT</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{tx('Título del track (opcional)', 'Track title (optional)', 'Título do track (opcional)')}</label>
                <input
                  type="text"
                  value={trackTitle}
                  onChange={(e) => setTrackTitle(e.target.value)}
                  placeholder={tx('Mi canción nueva', 'My new song', 'Minha música nova')}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#E8720C]/50 text-sm"
                  data-testid="track-title-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{tx('Nombre de artista (opcional)', 'Artist name (optional)', 'Nome do artista (opcional)')}</label>
                <input
                  type="text"
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder={tx('Tu nombre artístico', 'Your artist name', 'Seu nome artístico')}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#E8720C]/50 text-sm"
                  data-testid="artist-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">{tx('Notas para el equipo (opcional)', 'Notes for the team (optional)', 'Notas para a equipe (opcional)')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder={tx('¿Algún género específico, mood o playlist preferida? Cuéntanos...', 'Any specific genre, mood or preferred playlist? Tell us...', 'Algum gênero, mood ou playlist preferida? Conte para nós...')}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#E8720C]/50 text-sm resize-none"
                data-testid="notes-input"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="promo-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !selectedPlan || !spotifyUrl.trim()}
              className="w-full py-4 bg-[#E8720C] hover:bg-[#C55A00] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
              data-testid="submit-promo-btn"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> {tx('Procesando...', 'Processing...', 'Processando...')}</>
              ) : !user ? (
                <>{tx('Inicia sesión para continuar', 'Sign in to continue', 'Faça login para continuar')} <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>{tx('Continuar al pago', 'Continue to payment', 'Continuar para pagamento')} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>

            <p className="text-center text-[10px] text-white/40">
              {tx(
                'Pago seguro vía PayPal o Crypto · Sin renovación automática · Garantía de inclusión',
                'Secure payment via PayPal or Crypto · No auto-renewal · Inclusion guarantee',
                'Pagamento seguro via PayPal ou Crypto · Sem renovação automática · Garantia de inclusão'
              )}
            </p>
          </form>
        </div>

        {/* Playlists list */}
        {playlists.length > 0 && (
          <div className="glass-card p-7 md:p-10 max-w-3xl mx-auto" data-testid="playlists-list">
            <div className="flex items-center gap-3 mb-5">
              <ListMusic className="w-6 h-6 text-[#E8720C]" />
              <h2 className="text-xl font-bold">{tx('Nuestras playlists curadas', 'Our curated playlists', 'Nossas playlists curadas')}</h2>
            </div>
            <p className="text-sm text-white/55 mb-5">
              {tx(
                'Tu track puede aparecer en cualquiera de estas playlists oficiales OG MUSIC, según el género y el plan que elijas.',
                'Your track can appear on any of these official OG MUSIC playlists, depending on genre and the plan you pick.',
                'Seu track pode aparecer em qualquer dessas playlists oficiais OG MUSIC, conforme o gênero e o plano escolhido.'
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {playlists.map((pl, i) => (
                <a
                  key={i}
                  href={pl.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#E8720C]/30 hover:bg-white/[0.06] transition-all group"
                  data-testid={`playlist-link-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-green-500/15 flex items-center justify-center shrink-0">
                      <Music className="w-4 h-4 text-green-400" />
                    </div>
                    <span className="text-sm font-medium truncate">{pl.name}</span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-[#E8720C] shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Trust footer */}
        <div className="text-center mt-14 text-xs text-white/40 max-w-xl mx-auto">
          <p>
            ¿Preguntas? Escríbenos al chat. Una vez confirmado el pago, nuestro equipo curador
            revisa tu track en el plazo prometido por el plan y te notificamos por email cuando esté en playlist.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlaylistPromoPage;
